import { formatDate, formatAge } from "app/main/utils/dateUtils";
import Icon from "@material-ui/core/Icon";
import Typography from "@material-ui/core/Typography";
import moment from "moment";
import { convertSearchRequestToFilters, saveSearchesSessionStorage } from "app/main/searchV3/utils/searchUtils";

const getStateColor = (state) => {
	switch (state) {
		case "FINISHED":
		case "FINISHED_PRELOADED":
			return "text-green-700";
		case "FINISHED_WITH_FAILURES":
		case "PARTIALLY_FINISHED":
			return "text-orange-700";
		case "PROCESSING":
		case "QUEUEING":
			return "text-blue-700";
		case "EXPIRED":
			return "text-grey-500";
		case "FAILED":
		case "FAILED_STUCK":
			return "text-red-700";
		default:
			return "text-grey-700";
	}
};

const getExpirationReasonLabel = (reason) => {
	switch (reason) {
		case "PREV_HAS_FAILURES":
			return "Failures";
		case "USER_OFFLINE":
			return "Offline";
		case "USER_CLOSE":
			return "Closed";
		case "FORCE_EXPIRED":
			return "Force Expired";
		default:
			return reason;
	}
};

const getOverallStatus = (data) => {
	const allBrokers = [
		data?.dat,
		data?.smarthop,
		data?.chrobinson,
		data?.uber,
		data?.coyote,
		data?.loadsmart,
		data?.parade,
		data?.newtrul,
		data?.echo,
	];

	let queuedJob = allBrokers.filter((item) => item?.status === "QUEUED");
	let finishedJob = allBrokers.filter((item) => item?.status === "FINISHED");
	let failedJob = allBrokers.filter((item) => item?.status === "FAILED");

	let finishedRefreshJobs = [];
	let failedRefreshJobs = [];

	finishedJob.forEach((broker) => {
		if (broker.refreshHistory?.length > 0) {
			finishedRefreshJobs.push(...broker.refreshHistory.filter((item) => item?.status === "FINISHED"));
			failedRefreshJobs.push(...broker.refreshHistory.filter((item) => item?.status === "FAILED"));
		}
	});

	return (
		<div className="flex flex-row">
			{queuedJob.length > 0 && (
				<>
					<Icon className="text-orange">hourglass_top</Icon>
					<Typography color="inherit" variant="body2" className="p-4 font-light mr-8">
						{queuedJob.length}
					</Typography>
				</>
			)}
			{finishedJob.length > 0 && (
				<>
					<Icon className="text-green">check_circle</Icon>
					<Typography color="inherit" variant="body2" className="p-4 font-light">
						{finishedJob.length}
					</Typography>
				</>
			)}
			{failedJob.length > 0 && (
				<>
					<Icon className="text-red ml-1">error_outline</Icon>
					<Typography color="inherit" variant="body2" className="p-4 font-light">
						{failedJob.length}
					</Typography>
				</>
			)}
			{finishedRefreshJobs.length > 0 && (
				<>
					<Icon className="text-green ml-1">refresh</Icon>
					<Typography color="inherit" variant="body2" className="p-4 font-light">
						{finishedRefreshJobs.length}
					</Typography>
				</>
			)}
			{failedRefreshJobs.length > 0 && (
				<>
					<Icon className="text-red ml-1">refresh</Icon>
					<Typography color="inherit" variant="body2" className="p-4 font-light">
						{failedRefreshJobs.length}
					</Typography>
				</>
			)}
		</div>
	);
};

// Helper function to launch a search with the same filters and preserve search ID
const handleSearchWithSameFilters = async (searchRequest, history, dispatch) => {
	try {
		const searchFilters = convertSearchRequestToFilters(searchRequest);
		await saveSearchesSessionStorage(searchFilters);
		// Redirect to the specific search ID so users can view the search results
		// while having the filters pre-loaded in session storage
		history.push(`/search?id=${searchRequest._id}`);
	} catch (error) {
		console.error("Error launching search with same filters:", error);
	}
};

const carrierSearchesList = (dispatch) => ({
	urlGET: "api/search/carrier/:carrierId/requests",
	idKey: "_id",
	listenEvent: "searchRevision",
	segmentKey: "CARRIER_SEARCHES",
	content: {
		grid: {
			tilesPerRow: 2,
			itemsPerRow: 2,
		},
		table: {
			variant: "skinny",
			orderBy: ["createdAt", "updatedAt", "expiresAt", "origin", "destination", "availStart"],
		},
		pagination: {
			enabled: true,
			resetScroll: true,
		},
		order: {
			defaut: {
				key: "updatedAt",
				dir: "desc",
			},
		},
		defaults: {
			// Initial value of fields
			initial: {
				filters: {
					createdAt: "__DATE_DAYS_GT__7",
					expirationStatus: "ACTIVE",
				},
				groups: {},
			},
			// Additional quick filters
			items: [
				{
					label: "Active",
					filters: {
						expirationStatus: "ACTIVE",
						createdAt: "__DATE_DAYS_GT__7",
					},
				},
				{
					label: "All",
					filters: {
						expirationStatus: "__NOT_SELECTED__",
						createdAt: "__DATE_DAYS_GT__7",
					},
				},
				{
					label: "Expired",
					filters: {
						expirationStatus: "EXPIRED",
						createdAt: "__DATE_DAYS_GT__7",
					},
				},
			],
		},
		filters: {
			search: true,
			items: [
				{
					key: "expirationStatus",
					type: "select",
					label: "Status",
					defaultValue: "ACTIVE",
					options: [
						{
							value: "ACTIVE",
							label: "Active",
						},
						{
							value: "__NOT_SELECTED__",
							label: "All",
						},
						{
							value: "EXPIRED",
							label: "Expired",
						},
					],
				},
				{
					key: "truck",
					type: "autocomplete",
					label: "Truck",
					translate: "",
					field: {
						noErrorMessage: true,
						classes: {},
						variant: undefined,
					},
					autocomplete: {
						provider: "smarthop",
						url: "api/profile/trucks",
						params: {
							options_active_only: true,
							options_format_show_driver_phone: true,
						},
						preloadDefault: true,
						preloadImmedately: true,
						listenEvent: "profileRevision",
					},
					rules: {
						params: {
							dataRef: "carrierId",
							paramKey: "options_carrier_id_search",
						},
					},
				},
				{
					key: "trigger",
					type: "select",
					label: "Trigger",
					defaultValue: "__NOT_SELECTED__",
					options: [
						{
							value: "__NOT_SELECTED__",
							label: "All",
						},
						{
							value: "USER",
							label: "User",
						},
						{
							value: "MARKET_MONITOR",
							label: "Market Monitor",
						},
						{
							value: "MARKET_SCAN",
							label: "Market Scan",
						},
					],
				},
				{
					key: "uiStatus",
					type: "select",
					label: "Activity",
					defaultValue: "__NOT_SELECTED__",
					options: [
						{
							value: "__NOT_SELECTED__",
							label: "Not Selected",
						},
						{
							value: "__ONLY_ONLINE__",
							label: "Only Online",
						},
						{
							value: "__ONLY_HIDDEN__",
							label: "Only Hidden",
						},
						{
							value: "__ONLINE_AND_HIDDEN__",
							label: "Online and Hidden",
						},
					],
				},
				{
					key: "platform",
					type: "select",
					label: "Platform",
					defaultValue: "__NOT_SELECTED__",
					options: [
						{
							value: "__NOT_SELECTED__",
							label: "Not Selected",
						},
						{
							value: "WEB_MOBILE",
							label: "Web Mobile",
						},
						{
							value: "WEB_NATIVE",
							label: "Web Native",
						},
						{
							value: "WEB_DESKTOP",
							label: "Web Desktop",
						},
					],
					group: "internal",
				},
				{
					key: "createdAt",
					type: "select",
					label: "Created At",
					defaultValue: "__DATE_DAYS_GT__7",
					options: [
						{
							value: "__DATE_DAYS_GT__1",
							label: "Last 24 Hours",
						},
						{
							value: "__DATE_DAYS_GT__3",
							label: "Last 3 Days",
						},
						{
							value: "__DATE_DAYS_GT__7",
							label: "Last 7 Days",
						},
						{
							value: "__DATE_DAYS_GT__30",
							label: "Last 30 Days",
						},
					],
				},
			],
		},
		groups: {
			items: [
				{
					key: "internal",
					type: "checkbox",
					label: "Show Internal Data",
				},
				{
					key: "broker_metadata_dynamic_status",
					type: "checkbox",
					label: "Show Search Dynamic Status",
				},
			],
		},
		items: [
			{
				key: "_id",
				type: "id",
				label: "Search ID",
				group: "internal",
			},
			{
				key: "fingerprints",
				type: "text",
				label: "Fingerprint",
				group: "internal",
			},
			{
				key: "trigger",
				type: "text",
				label: "Trigger",
				group: "internal",
			},
			{
				key: "options",
				type: "text",
				label: "Options",
				group: "internal",
				builder: (item) => JSON.stringify(item.analytics?.options, null, 6),
				collapsibleLength: 40,
			},
			{
				key: "ip",
				type: "text",
				label: "IP",
				group: "internal",
			},
			{
				key: "updatedAt",
				type: "component",
				label: "Updated",
				builder: (item) => (
					<div className="flex flex-col">
						<span className="text-12 ">{moment(item.updatedAt).fromNow()}</span>
						<span className="text-11 text-grey-600">{formatDate(item.createdAt)}</span>
					</div>
				),
			},
			{
				type: "group",
				label: "UI Status",
				items: [
					{
						key: "uiStatus",
						type: "component",
						builder: (item) => (
							<div className="flex flex-col">
								<div className="flex flex-row items-center">
									{item.activeOnUI ? (
										<Typography
											color="inherit"
											variant="body2"
											className="px-5 mt-2 text-white text-12 font-medium bg-green rounded-8 my-5"
										>
											Online
										</Typography>
									) : item.hiddenOnUI ? (
										<Typography
											color="inherit"
											variant="body2"
											className="px-5 mt-2 text-white text-12 font-medium bg-orange rounded-8 my-5"
										>
											Hidden
										</Typography>
									) : (
										<Typography
											color="inherit"
											variant="body2"
											className="px-5 mt-2 text-white text-12 font-medium bg-grey rounded-8 my-5"
										>
											NA
										</Typography>
									)}
									{item.refreshAllowed && <Icon className="text-green ml-1">refresh</Icon>}
								</div>
								<span className="text-12">{item.lastPing ? formatAge(item.lastPing) : "-"}</span>
							</div>
						),
					},
				],
			},
			{ key: "platform", type: "text", label: "Platform", group: "internal" },
			{
				key: "origin",
				type: "text",
				label: "Origin",
			},
			{
				key: "availStart",
				type: "component",
				label: "Ready Date",
				builder: (item) => (
					<div className="flex flex-col">
						<span className="text-12">{item.availStart ? formatDate(item.availStart) : "-"}</span>
					</div>
				),
			},

			{
				key: "destination",
				type: "text",
				label: "Destination",
				builder: (item) => item?.destination || item?.destinationStates?.join(", ") || "Anywhere",
			},
			/*{
				key: "equipment",
				type: "text",
				label: "Equipment",
			},
			*/
			{
				label: "Status",
				type: "component",
				builder: (item) => {
					return getOverallStatus(item);
				},
			},
			{
				label: "Dynamic Status",
				type: "component",
				group: "internal",
				builder: (item) => {
					const state = item?.dynamicStatus?.state;
					return <span className={`font-medium ${getStateColor(state)}`}>{state || "UNKNOWN"}</span>;
				},
			},
			{
				key: "dynamicStatus",
				type: "text",
				label: "Dynamic Status",
				builder: (item) => JSON.stringify(item.dynamicStatus, null, 6),
				collapsibleLength: 40,
				group: "broker_metadata_dynamic_status",
			},
			{
				key: "expiresAt",
				type: "component",
				label: "Expiration",
				builder: (item) => {
					const isExpired = moment(item.expiresAt).isBefore(moment());
					return (
						<div className="flex flex-col">
							<span className={`text-12 ${isExpired ? "text-grey-500" : ""}`}>{formatDate(item.expiresAt)}</span>
							{item.expirationReason ? (
								<Typography
									color="inherit"
									variant="body2"
									className="px-5 mt-2 text-white text-10 font-medium bg-red rounded-8"
								>
									{getExpirationReasonLabel(item.expirationReason)}
								</Typography>
							) : isExpired ? (
								<Typography
									color="inherit"
									variant="body2"
									className="px-5 mt-2 text-white text-10 font-medium bg-red rounded-8"
								>
									Expired
								</Typography>
							) : (
								<span className="text-11 text-green-600">Active</span>
							)}
						</div>
					);
				},
			},
			{
				key: "analytics",
				type: "component",
				label: "Results",
				builder: (item) => {
					const total = item?.analytics?.counters?.total ?? 0;
					const best = item?.analytics?.counters?.best ?? 0;
					const hopbest = item?.analytics?.counters?.hopbest ?? 0;
					const newTotal = item?.analytics?.counters?.newTotal ?? 0;
					const deleted = item?.analytics?.counters?.deleted ?? 0;
					return (
						<div className="flex flex-col">
							<span className="text-12">
								New: {newTotal}/{total}
							</span>
							<span className="text-11 text-grey-600">
								Best: {best} / Forced: {hopbest}
							</span>
							<span className="text-11 text-grey-600">Deleted: {deleted}</span>
						</div>
					);
				},
			},
			{
				key: "agent",
				type: "component",
				label: "Agent",
				builder: (item) => {
					const contactedCalled = item?.analytics?.counters?.contacted ?? 0;
					const emailSent = item?.analytics?.counters?.emailSent ?? 0;
					const contactedEnd = item?.analytics?.counters?.contactedEnd ?? 0;
					const emailReplied = item?.analytics?.counters?.emailReplied ?? 0;
					const covered = item?.analytics?.counters?.covered ?? 0;
					const contacted = contactedCalled + emailSent;

					const hasActivity = contactedCalled > 0 || emailSent > 0 || contactedEnd > 0 || emailReplied > 0;
					return (
						<div className="flex flex-col">
							{hasActivity ? (
								<>
									<span className="text-12">
										Covered: {covered} / {contacted}
									</span>
									<span className="text-11 text-grey-600">
										Called: {contactedEnd} / {contactedCalled}
									</span>
									<span className="text-11 text-grey-600">
										Emailed: {emailReplied} / {emailSent}
									</span>
								</>
							) : (
								<span className="text-11 text-grey-500">-</span>
							)}
						</div>
					);
				},
			},
			{
				key: "rateStats",
				type: "component",
				label: "Rate Stats",
				builder: (item) => {
					const rateMedian = item?.analytics?.stats?.rate?.median;
					const rateTarget = item?.analytics?.stats?.rate?.q90;
					const rpmMedian = item?.analytics?.stats?.rpm?.median;
					const rpmTarget = item?.analytics?.stats?.rpm?.q90;
					const milesMedian = item?.analytics?.stats?.miles?.median;
					const milesTarget = item?.analytics?.stats?.miles?.q90;
					return (
						<div className="flex flex-col">
							{rateMedian ? (
								<>
									<span className="text-12">Rate: ${Math.round(rateMedian)}</span>
									<span className="text-11 text-grey-600">Top: ${Math.round(rateTarget)}</span>
									<span className="text-11 text-grey-600">RPM: ${rpmMedian?.toFixed(2) ?? "-"}</span>
									<span className="text-11 text-grey-600">Miles: {milesMedian ? Math.round(milesMedian) : "-"}</span>
								</>
							) : (
								<span className="text-11 text-grey-500">-</span>
							)}
						</div>
					);
				},
			},
			{
				key: "user__view",
				type: "user__view",
				label: "User",
				viewConfig: {
					showRole: true,
				},
			},
			{
				key: "truck__view",
				type: "truck__view",
				label: "Truck",
			},
			{
				key: "load_scout",
				type: "plan__view",
				label: "Load Scout",
				builder: (item) => item.plan__view,
				viewConfig: {
					readOnly: true,
					mode: "LOAD_SCOUNT",
				},
				table: { width: 140 },
			},
			{
				key: "trip__view",
				type: "trip__view",
				label: "Trip",
				viewConfig: {
					showStatus: true,
				},
			},
			{
				key: "menu",
				type: "menu",
				label: "Menu",
				items: [
					{
						key: "go_to_search",
						label: "Go to Search",
						icon: "search",
						dispatchAction: (item, dataIds, dispatch, history, snackbar) => {
							handleSearchWithSameFilters(item, history, dispatch);
						},
					},

					{
						key: "view_results",
						label: "View Full Results",
						newTab: true,
						redirectURLBuilder: (item) => `/admin/search/requests/${item._id}/details/user/${item.user}`,
					},
					{
						variant: "divider",
					},
					{
						key: "retrigger_search",
						label: "Re-Start Search",
						dialogBuilder: (item) => ({
							viewId: "CONFIRM_DIALOG",
							mode: "CREATE",
							dataIds: {
								title: "Re-Start Search",
								message: `This will resume the search using the same filters as search (${item._id}).\n\nOrigin: ${
									item.origin || "Any"
								}\nDestination: ${
									item.destination || item.destinationStates?.join(", ") || "Anywhere"
								}\n\n Options: ${JSON.stringify(item.analytics?.options, null, 6)}`,
								confirmLabel: "Re-Start",
								url: `api/search/requests/${item._id}/retrigger`,
								method: "POST",
								listenEvent: "searchRevision",
								body: {
									shouldResume: true,
									...(item.analytics?.options ? { options: item.analytics.options } : {}),
								},
							},
						}),
					},
					{
						key: "duplicate_search",
						label: "Duplicate Search",
						dialogBuilder: (item) => ({
							viewId: "CONFIRM_DIALOG",
							mode: "CREATE",
							dataIds: {
								title: "Duplicate Search",
								message: `This will create a new search using the same filters as search (${
									item._id
								}) but not the same options.\n\nOrigin: ${item.origin || "Any"}\nDestination: ${
									item.destination || item.destinationStates?.join(", ") || "Anywhere"
								}`,
								confirmLabel: "Re-Start",
								url: `api/search/requests/${item._id}/retrigger`,
								method: "POST",
								listenEvent: "searchRevision",
							},
						}),
					},
					{
						variant: "divider",
					},
					{
						key: "force_expire",
						label: "Stop Search",
						dialogBuilder: (item) => ({
							viewId: "CONFIRM_DIALOG",
							mode: "DELETE",
							dataIds: {
								title: "Stop Search",
								message: `Are you sure you want to stop the agent for this search (${item._id})? This action cannot be undone.`,
								confirmLabel: "Stop Search",
								url: `api/search/requests/${item._id}/force-expire`,
								listenEvent: "searchRevision",
							},
						}),
					},
					{
						variant: "divider",
					},
					{
						key: "delete_search_job",
						label: "Delete Search Job",
						icon: "delete_forever",
						disabled: (item) => moment(item.expiresAt).isAfter(moment()),
						dialogBuilder: (item) => ({
							viewId: "CONFIRM_DIALOG",
							mode: "DELETE",
							dataIds: {
								title: "Delete Search Job",
								message: `Warning: This will permanently delete the search job (${
									item._id
								}) and all associated data.\n\nOrigin: ${item.origin || "Any"}\nDestination: ${
									item.destination || item.destinationStates?.join(", ") || "Anywhere"
								}\n\nThis action cannot be undone. Only expired searches can be deleted.`,
								confirmLabel: "Delete",
								url: `api/search/requests/${item._id}`,
								method: "DELETE",
								listenEvent: "searchRevision",
							},
						}),
					},
				],
			},
		],
	},
});

export default carrierSearchesList;
